import { OffsetRange } from '../core/offsetRange.js';
import { StringEdit, StringReplacement } from './stringEdit.js';

/**
 * Annotation attached to a replacement. `join` decides whether two
 * adjacent annotations can be merged during `normalize()`.
 */
export interface IEditData<T> {
	join(other: T): T | undefined;
}

/**
 * An atomic replacement carrying user-defined metadata.
 */
export class AnnotatedStringReplacement<T extends IEditData<T>> {
	constructor(
		readonly range: OffsetRange,
		readonly newText: string,
		readonly data: T,
	) { }

	get isEmpty(): boolean {
		return this.range.isEmpty && this.newText.length === 0;
	}

	toString(): string {
		return `${this.range}->${JSON.stringify(this.newText)}#${this.data}`;
	}
}

/**
 * A set of annotated replacements applied in parallel.
 *
 * Invariants: same as `StringEdit` (sorted by `range.start`, non-overlapping,
 * shared coordinate space).
 */
export class AnnotatedStringEdit<T extends IEditData<T>> {
	static empty<T extends IEditData<T>>(): AnnotatedStringEdit<T> {
		return new AnnotatedStringEdit<T>([]);
	}

	static single<T extends IEditData<T>>(replacement: AnnotatedStringReplacement<T>): AnnotatedStringEdit<T> {
		return new AnnotatedStringEdit([replacement]);
	}

	static of<T extends IEditData<T>>(
		replacements: readonly AnnotatedStringReplacement<T>[],
	): AnnotatedStringEdit<T> {
		for (let i = 1; i < replacements.length; i++) {
			const prev = replacements[i - 1];
			const cur = replacements[i];
			if (prev.range.endExclusive > cur.range.start) {
				throw new Error(
					`AnnotatedStringEdit.of: replacements must be sorted and non-overlapping (` +
					`${prev.range} vs ${cur.range})`
				);
			}
		}
		return new AnnotatedStringEdit(replacements);
	}

	private constructor(
		readonly replacements: readonly AnnotatedStringReplacement<T>[],
	) { }

	get isEmpty(): boolean {
		return this.replacements.length === 0;
	}

	apply(text: string): string {
		if (this.replacements.length === 0) { return text; }
		const parts: string[] = [];
		let cursor = 0;
		for (const r of this.replacements) {
			parts.push(text.substring(cursor, r.range.start));
			parts.push(r.newText);
			cursor = r.range.endExclusive;
		}
		parts.push(text.substring(cursor));
		return parts.join('');
	}

	inverse(input: string, invertData: (data: T) => T): AnnotatedStringEdit<T> {
		const out: AnnotatedStringReplacement<T>[] = [];
		let delta = 0;
		for (const r of this.replacements) {
			const newStart = r.range.start + delta;
			const newEnd = newStart + r.newText.length;
			const oldText = input.substring(r.range.start, r.range.endExclusive);
			out.push(new AnnotatedStringReplacement(
				new OffsetRange(newStart, newEnd),
				oldText,
				invertData(r.data),
			));
			delta += r.newText.length - r.range.length;
		}
		return new AnnotatedStringEdit(out);
	}

	/**
	 * Joins touching replacements when their annotations join. Drops no-ops.
	 */
	normalize(): AnnotatedStringEdit<T> {
		const out: AnnotatedStringReplacement<T>[] = [];
		for (const r of this.replacements) {
			if (r.isEmpty) { continue; }
			const prev = out[out.length - 1];
			if (prev && prev.range.endExclusive === r.range.start) {
				const joined = prev.data.join(r.data);
				if (joined !== undefined) {
					out[out.length - 1] = new AnnotatedStringReplacement(
						new OffsetRange(prev.range.start, r.range.endExclusive),
						prev.newText + r.newText,
						joined,
					);
					continue;
				}
			}
			out.push(r);
		}
		return new AnnotatedStringEdit(out);
	}

	mapData<U extends IEditData<U>>(f: (data: T) => U): AnnotatedStringEdit<U> {
		return new AnnotatedStringEdit(
			this.replacements.map(r => new AnnotatedStringReplacement(r.range, r.newText, f(r.data))),
		);
	}

	stripData(): StringEdit {
		return StringEdit.of(
			this.replacements.map(r => new StringReplacement(r.range, r.newText)),
		);
	}

	toString(): string {
		return `AnnotatedStringEdit[${this.replacements.map(r => r.toString()).join(', ')}]`;
	}
}

/**
 * Diff result annotation.
 *
 * Carries flags describing the nature of the replacement. For now only
 * `isFormattingChange` is exposed; more flags (e.g. move source/target,
 * semantic category) can be added later.
 */
export class DiffAnnotation implements IEditData<DiffAnnotation> {
	static readonly change: DiffAnnotation = new DiffAnnotation(false);
	static readonly formatting: DiffAnnotation = new DiffAnnotation(true);

	private constructor(
		/** Whether the replacement is purely a formatting change (whitespace, indentation, etc.). */
		readonly isFormattingChange: boolean,
	) { }

	join(other: DiffAnnotation): DiffAnnotation | undefined {
		if (this.isFormattingChange !== other.isFormattingChange) {
			return undefined;
		}
		return this;
	}

	toString(): string {
		return this.isFormattingChange ? 'formatting' : 'change';
	}
}
